import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-calendar-widget',
  imports: [CommonModule, MatCardModule, MatIconModule, MatButtonModule],
  templateUrl: './calendar-widget.html',
  styleUrl: './calendar-widget.css',
})
export class CalendarWidget {
  /** Today's date, used to highlight the current day. */
  today = new Date();

  /** The first day of the currently displayed month. */
  current = new Date(this.today.getFullYear(), this.today.getMonth(), 1);

  /**
   * Returns a formatted month/year label for the calendar header.
   * @returns {string} e.g. 'May 2026'
   */
  get monthLabel(): string {
    return this.current.toLocaleString('default', { month: 'long', year: 'numeric' });
  }

  /**
   * Builds the grid cell array for the current month.
   * Leading nulls pad the first row so day 1 lands on the correct weekday column.
   * @returns {(number | null)[]}
   */
  get days(): (number | null)[] {
    const year = this.current.getFullYear();
    const month = this.current.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const cells: (number | null)[] = Array(firstDay).fill(null);
    for (let i = 1; i <= daysInMonth; i++) cells.push(i);
    return cells;
  }

  /**
   * Returns true if the given day number matches today's date in the currently displayed month.
   * @param {number | null} day - A calendar grid cell value (null for padding cells).
   * @returns {boolean}
   */
  isToday(day: number | null): boolean {
    if (!day) return false;
    return (
      day === this.today.getDate() &&
      this.current.getMonth() === this.today.getMonth() &&
      this.current.getFullYear() === this.today.getFullYear()
    );
  }

  /**
   * Navigates the calendar back one month.
   * @returns {void}
   */
  prevMonth(): void {
    this.current = new Date(this.current.getFullYear(), this.current.getMonth() - 1, 1);
  }

  /**
   * Navigates the calendar forward one month.
   * @returns {void}
   */
  nextMonth(): void {
    this.current = new Date(this.current.getFullYear(), this.current.getMonth() + 1, 1);
  }
}
